import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../hooks/redux';
import { useApi } from '../../hooks/useApi';
import api from '../../services/api';
import MessageThread from '../../components/MessageThread';
import CompanyLogo from '../../components/CompanyLogo';
import { EmptyState, Loading, Notice, StatusPill } from '../../components/ui';
import { COMPANY_STATUS, companyDisplayName, companyTitle, timeAgo } from '../../lib/format';
import type { Company, Message } from '../../types/workspace';

type LastMessage = {
  id: string;
  body: string;
  kind: 'MESSAGE' | 'SYSTEM';
  createdAt: string;
  author: { id: string; name: string | null; email: string; role: string } | null;
} | null;

type CompanyRow = Company & { lastMessage?: LastMessage };

type CompanyList = { companies: CompanyRow[] };
type Conversation = {
  company: Pick<Company, 'id' | 'name' | 'logicalName' | 'workspaceLabel' | 'hasLogo' | 'status' | 'countryCode' | 'customer' | 'assignedAdmin'>;
  messages: Message[];
};

function previewText(msg: LastMessage): string {
  if (!msg) return 'No messages yet';
  if (msg.kind === 'SYSTEM') return msg.body;
  const who = msg.author?.name || msg.author?.email || 'Someone';
  const body = msg.body.replace(/\s+/g, ' ').trim();
  return `${who}: ${body.length > 72 ? `${body.slice(0, 72)}…` : body}`;
}

export default function Messages() {
  const user = useAppSelector((s) => s.auth.user);
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [companyId, setCompanyId] = useState(params.get('company') || '');
  const companiesApi = useApi<CompanyList>('/admin/companies?scope=all&messages=1', 15000);
  const conversationApi = useApi<Conversation>(companyId ? `/admin/companies/${companyId}/messages` : null, 10000);
  const companies = companiesApi.data?.companies || [];

  const sorted = useMemo(() => {
    return [...companies].sort((a, b) => {
      const at = a.lastMessage?.createdAt || a.updatedAt || '';
      const bt = b.lastMessage?.createdAt || b.updatedAt || '';
      return bt.localeCompare(at);
    });
  }, [companies]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((c) =>
      [c.logicalName, c.name, c.workspaceLabel, c.email, c.customer?.name, c.customer?.email, c.countryCode]
        .some((v) => v?.toLowerCase().includes(q)),
    );
  }, [sorted, query]);

  useEffect(() => {
    if (!companyId && sorted.length) {
      const first = sorted[0].id;
      setCompanyId(first);
      setParams({ company: first }, { replace: true });
    }
    if (companyId && sorted.length && !sorted.some((c) => c.id === companyId)) {
      const first = sorted[0]?.id || '';
      setCompanyId(first);
      if (first) setParams({ company: first }, { replace: true });
    }
  }, [sorted, companyId, setParams]);

  const selectCompany = (id: string) => {
    setCompanyId(id);
    setParams({ company: id }, { replace: true });
  };

  if (companiesApi.loading) return <Loading />;
  if (companiesApi.error) return <Notice tone="rose">{companiesApi.error}</Notice>;

  const active = conversationApi.data?.company;
  const title = active ? companyTitle(active) : '';
  const status = active ? COMPANY_STATUS[active.status] || { label: active.status, tone: 'slate' as const } : null;

  return (
    <div className="-mx-4 -my-5 flex h-[calc(100vh-4.5rem)] flex-col sm:-mx-6 sm:-my-6">
      <div className="flex min-h-0 flex-1 overflow-hidden bg-white" style={{ borderTop: '1px solid #fce7f3' }}>
        {/* Conversation list */}
        <aside className={`${companyId ? 'hidden lg:flex' : 'flex'} w-full shrink-0 flex-col border-r border-[#fce7f3] lg:w-[320px] xl:w-[360px]`}>
          <div className="shrink-0 border-b border-[#fce7f3] px-4 py-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#c084a0]">Consult</p>
            <h1 className="mt-0.5 text-xl font-black tracking-tight text-[#6b0030]">Messages</h1>
            <p className="mt-1 text-xs text-[#b0708f]">Shared workspace threads with each company.</p>
            <label className="sr-only" htmlFor="message-search">Search conversations</label>
            <input
              id="message-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by Micus name…"
              className="mt-3 w-full rounded-xl border border-[#fce7f3] bg-[#fffafc] px-3 py-2 text-sm text-[#4a1a30] outline-none transition focus:border-[#d6006e] focus:bg-white"
            />
          </div>

          {visible.length === 0 ? (
            <div className="p-4">
              <EmptyState title={companies.length ? 'No matches' : 'No companies yet'} body={companies.length ? 'Try another search.' : 'Conversations appear once a company is registered.'} />
            </div>
          ) : (
            <ul className="min-h-0 flex-1 overflow-y-auto py-1" role="listbox" aria-label="Company conversations">
              {visible.map((company) => {
                const selected = company.id === companyId;
                const display = companyTitle(company);
                const last = company.lastMessage;
                return (
                  <li key={company.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => selectCompany(company.id)}
                      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition ${
                        selected ? 'bg-[#fdf2f8]' : 'hover:bg-[#fffafc]'
                      }`}
                      style={selected ? { boxShadow: 'inset 3px 0 0 #d6006e' } : undefined}
                    >
                      <CompanyLogo companyId={company.id} name={companyDisplayName(company)} hasLogo={company.hasLogo} className="mt-0.5 h-10 w-10 rounded-xl text-sm" />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className={`truncate text-sm ${selected ? 'font-black text-[#d6006e]' : 'font-bold text-[#6b0030]'}`}>{display}</span>
                          <span className="shrink-0 text-[10px] font-medium text-[#c084a0]">{timeAgo(last?.createdAt || company.updatedAt)}</span>
                        </span>
                        <span className="mt-0.5 block truncate text-[11px] text-[#b0708f]">{previewText(last || null)}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* Thread */}
        <section className={`${companyId ? 'flex' : 'hidden lg:flex'} min-w-0 flex-1 flex-col`}>
          {!companyId ? (
            <div className="flex flex-1 items-center justify-center p-8">
              <EmptyState title="Pick a conversation" body="Choose a company on the left to open the shared message thread." />
            </div>
          ) : conversationApi.loading && !conversationApi.data ? (
            <Loading />
          ) : conversationApi.error || !conversationApi.data || !active ? (
            <div className="p-5"><Notice tone="rose">{conversationApi.error || 'Could not load messages.'}</Notice></div>
          ) : (
            <>
              <header className="flex shrink-0 items-center gap-3 border-b border-[#fce7f3] bg-white/95 px-4 py-3 backdrop-blur sm:px-5">
                <button
                  type="button"
                  className="rounded-lg px-2 py-1.5 text-xs font-bold text-[#d6006e] hover:bg-[#fdf2f8] lg:hidden"
                  onClick={() => {
                    setCompanyId('');
                    setParams({}, { replace: true });
                  }}
                >
                  ← Back
                </button>
                <CompanyLogo companyId={active.id} name={companyDisplayName(active)} hasLogo={active.hasLogo} className="h-11 w-11 rounded-xl text-base" />
                <div className="min-w-0 flex-1">
                  <h2 className="truncate text-base font-black text-[#6b0030]">{title}</h2>
                  <p className="truncate text-xs text-[#b0708f]">
                    {active.customer?.name || active.customer?.email || 'Company contact'}
                    {active.countryCode ? ` · ${active.countryCode}` : ''}
                  </p>
                </div>
                {status && <StatusPill label={status.label} tone={status.tone} />}
              </header>
              <MessageThread
                messages={conversationApi.data.messages}
                currentUserId={user?.id}
                placeholder={`Message ${companyDisplayName(active)}…`}
                onSend={async (body) => {
                  await api.post(`/admin/companies/${companyId}/messages`, { body });
                  await Promise.all([conversationApi.reload(true), companiesApi.reload(true)]);
                }}
              />
            </>
          )}
        </section>
      </div>
    </div>
  );
}
